// Scoreboard client — points earned for completing configurations (/api/points).
//
// Every call here is BEST-EFFORT and never throws. Points are a nicety bolted
// onto a production quoting tool: a scoreboard outage must not surface an error
// to the user, and must never be able to interfere with a submit. Failures
// resolve to null / not-awarded and are logged to the console only.

const API_BASE = "/api";

export const POINTS_PER_CONFIG = 100;

export type LeaderboardRow = {
  rank: number;
  userId: string;
  userName: string;
  points: number;
  configs: number;
  lastEarned: string | null;
};

export type MyStanding = {
  userId: string;
  userName: string;
  points: number;
  configs: number;
  // Always false now — an excluded user is not scored at all, so they have no
  // standing to return. Kept so the shape is explicit. See meExcluded below.
  excluded: boolean;
  rank: number | null;
};

export type Leaderboard = {
  leaderboard: LeaderboardRow[];
  me: MyStanding | null;
  // True when the caller is on the exclusion list: they are NOT scored, so they
  // have no rows and no standing. Lets the UI explain the absence.
  meExcluded: boolean;
};

/** Award points for a completed configuration. Idempotent per (user, item) on the
 *  server, so re-submitting the same item earns nothing further. */
export async function awardConfigurationPoints(opts: {
  userId: string | null;
  userName: string | null;
  itemId: string | null;
  itemName: string | null;
}): Promise<{ awarded: boolean; points: number; total: number } | null> {
  if (!opts.userId) return null; // launched without an operator — nobody to credit
  try {
    const res = await fetch(`${API_BASE}/points`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        userId: opts.userId,
        userName: opts.userName,
        points: POINTS_PER_CONFIG,
        reason: "configuration",
        itemId: opts.itemId,
        itemName: opts.itemName,
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      awarded?: boolean;
      points?: number;
      total?: number;
    };
    if (!res.ok || body.ok === false) return null;
    return {
      awarded: body.awarded === true,
      points: Number(body.points ?? 0),
      total: Number(body.total ?? 0),
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[cpq2] points award failed (ignored):", e);
    return null;
  }
}

/** The leaderboard, plus the caller's own standing when `userId` is supplied. */
export async function fetchLeaderboard(userId: string | null): Promise<Leaderboard | null> {
  try {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
    const res = await fetch(`${API_BASE}/points${qs}`, { headers: { Accept: "application/json" } });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      leaderboard?: LeaderboardRow[];
      me?: MyStanding | null;
      meExcluded?: boolean;
    };
    if (!res.ok || body.ok === false) return null;
    return {
      leaderboard: body.leaderboard ?? [],
      me: body.me ?? null,
      meExcluded: body.meExcluded === true,
    };
  } catch {
    return null;
  }
}
